import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Sidebar } from "@/components/Sidebar";
import AIChat from "@/components/AIChat";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import {
  DollarSign, Calculator, FileCheck, AlertCircle, CheckCircle, Clock,
  MessageSquare, Brain, Bot, ChevronRight, Sparkles, Shield, FileText
} from "lucide-react";

export default function FIPage() {
  const [deals, setDeals] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showChat, setShowChat] = useState(false);
  
  // Calculator state
  const [price, setPrice] = useState(35000);
  const [downPayment, setDownPayment] = useState(5000);
  const [tradeIn, setTradeIn] = useState(0);
  const [term, setTerm] = useState(60);
  const [apr, setApr] = useState(5.9);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [dealsRes, workersRes] = await Promise.all([
        axios.get(`${API}/deals`),
        axios.get(`${API}/digital-workers/fi`)
      ]);
      setDeals(dealsRes.data);
      setWorkers(workersRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const calculatePayment = () => {
    const principal = price - downPayment - tradeIn;
    const monthlyRate = apr / 100 / 12;
    const payment = principal * (monthlyRate * Math.pow(1 + monthlyRate, term)) / (Math.pow(1 + monthlyRate, term) - 1);
    return isNaN(payment) ? 0 : payment.toFixed(2);
  };

  const getComplianceColor = (status) => {
    const colors = {
      passed: "bg-emerald-500/10 text-emerald-500",
      issues: "bg-red-500/10 text-red-500",
      pending: "bg-amber-500/10 text-amber-500"
    };
    return colors[status] || colors.pending;
  };

  const getFundingColor = (status) => {
    const colors = {
      funded: "bg-emerald-500/10 text-emerald-500",
      pending: "bg-amber-500/10 text-amber-500",
      not_submitted: "bg-slate-500/10 text-slate-400"
    };
    return colors[status] || colors.not_submitted;
  };

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <div className="dashboard-content">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display text-3xl font-bold text-foreground flex items-center gap-3" data-testid="fi-title">
              <DollarSign className="w-8 h-8 text-amber-500" />
              F&I
            </h1>
            <p className="text-muted-foreground">Finance, Insurance & Deal Compliance Management</p>
          </div>
          <Button variant="outline" onClick={() => setShowChat(true)} className="gap-2">
            <Brain className="w-4 h-4" /> AI Assistant
          </Button>
        </div>

        <Tabs defaultValue="calculator" className="space-y-6">
          <TabsList>
            <TabsTrigger value="calculator" className="gap-2"><Calculator className="w-4 h-4" /> Payment Calculator</TabsTrigger>
            <TabsTrigger value="deals" className="gap-2"><FileText className="w-4 h-4" /> Active Deals</TabsTrigger>
            <TabsTrigger value="workers" className="gap-2"><Bot className="w-4 h-4" /> Digital Workers</TabsTrigger>
          </TabsList>

          {/* Calculator Tab */}
          <TabsContent value="calculator" className="space-y-6">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calculator className="w-5 h-5 text-amber-500" />
                    Payment Calculator
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Vehicle Price: ${price.toLocaleString()}</label>
                    <Slider value={[price]} onValueChange={(v) => setPrice(v[0])} min={10000} max={100000} step={500} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Down Payment: ${downPayment.toLocaleString()}</label>
                    <Slider value={[downPayment]} onValueChange={(v) => setDownPayment(v[0])} min={0} max={30000} step={500} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium">Trade-In Value: ${tradeIn.toLocaleString()}</label>
                    <Slider value={[tradeIn]} onValueChange={(v) => setTradeIn(v[0])} min={0} max={40000} step={500} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium">Term (months)</label>
                      <Input type="number" value={term} onChange={(e) => setTerm(Number(e.target.value))} />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium">APR (%)</label>
                      <Input type="number" step="0.1" value={apr} onChange={(e) => setApr(Number(e.target.value))} />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/20">
                <CardContent className="p-8 flex flex-col items-center justify-center h-full">
                  <div className="text-center">
                    <div className="text-muted-foreground mb-2">Estimated Monthly Payment</div>
                    <div className="text-6xl font-display font-bold text-foreground">${calculatePayment()}</div>
                    <div className="text-sm text-muted-foreground mt-2">for {term} months at {apr}% APR</div>
                  </div>
                  <div className="grid grid-cols-2 gap-8 mt-8 text-center">
                    <div>
                      <div className="text-sm text-muted-foreground">Amount Financed</div>
                      <div className="text-xl font-bold">${(price - downPayment - tradeIn).toLocaleString()}</div>
                    </div>
                    <div>
                      <div className="text-sm text-muted-foreground">Total Interest</div>
                      <div className="text-xl font-bold">${((calculatePayment() * term) - (price - downPayment - tradeIn)).toLocaleString()}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* F&I Products */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-amber-500" />
                  F&I Products
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-3 gap-4">
                  {[
                    { name: "Extended Warranty", price: "$1,999", desc: "Bumper-to-bumper coverage up to 100K miles" },
                    { name: "GAP Insurance", price: "$899", desc: "Covers loan balance if vehicle is totaled" },
                    { name: "Tire & Wheel Protection", price: "$599", desc: "Covers damage from road hazards" },
                    { name: "Paint Protection", price: "$499", desc: "Professional ceramic coating protection" },
                    { name: "Theft Protection", price: "$399", desc: "Vehicle recovery system + insurance deductible" },
                    { name: "Maintenance Plan", price: "$799", desc: "Prepaid maintenance for 3 years / 36K miles" },
                  ].map((product, idx) => (
                    <Card key={idx} className="hover:border-amber-500/30 transition-all cursor-pointer">
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start mb-2">
                          <div className="font-semibold text-foreground">{product.name}</div>
                          <Badge className="bg-amber-500/10 text-amber-500">{product.price}</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground">{product.desc}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Deals Tab */}
          <TabsContent value="deals" className="space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: "Active Deals", value: deals.filter(d => d.status !== "funded").length, icon: FileText, color: "text-amber-500" },
                { label: "Compliance Issues", value: deals.filter(d => d.compliance_status === "issues").length, icon: AlertCircle, color: "text-red-500" },
                { label: "Pending Funding", value: deals.filter(d => d.funding_status === "pending").length, icon: Clock, color: "text-blue-500" },
                { label: "Funded This Month", value: deals.filter(d => d.funding_status === "funded").length, icon: CheckCircle, color: "text-emerald-500" },
              ].map((stat, idx) => (
                <Card key={idx}>
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg ${stat.color.replace('text-', 'bg-')}/10 flex items-center justify-center`}>
                      <stat.icon className={`w-5 h-5 ${stat.color}`} />
                    </div>
                    <div>
                      <div className="text-2xl font-bold">{stat.value}</div>
                      <div className="text-xs text-muted-foreground">{stat.label}</div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Deals List */}
            <div className="space-y-4">
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Loading deals...</div>
              ) : deals.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">No active deals</div>
              ) : (
                deals.map((deal) => (
                  <Card key={deal.id} className="hover:border-amber-500/30 transition-all" data-testid={`deal-${deal.id}`}>
                    <CardContent className="p-5">
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div>
                          <div className="font-semibold text-lg text-foreground">{deal.customer_name}</div>
                          <div className="text-sm text-muted-foreground">{deal.vehicle_info}</div>
                        </div>

                        <div className="flex items-center gap-6">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-foreground">${deal.monthly_payment}/mo</div>
                            <div className="text-xs text-muted-foreground">{deal.term_months}mo @ {deal.apr}%</div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Badge className={getComplianceColor(deal.compliance_status)}>
                              {deal.compliance_status === "passed" ? <CheckCircle className="w-3 h-3 mr-1" /> : <AlertCircle className="w-3 h-3 mr-1" />}
                              {deal.compliance_status}
                            </Badge>
                            <Badge className={getFundingColor(deal.funding_status)}>
                              {deal.funding_status.replace('_', ' ')}
                            </Badge>
                          </div>

                          <div className="w-12 h-12 rounded-full bg-amber-500/10 flex items-center justify-center">
                            <span className="text-amber-500 font-bold">{deal.ai_deal_score}</span>
                          </div>
                        </div>
                      </div>

                      {/* Compliance Issues */}
                      {deal.compliance_issues?.length > 0 && (
                        <div className="mt-4 p-3 rounded-lg bg-red-500/5 border border-red-500/20">
                          <div className="flex items-center gap-2 text-red-500 font-medium mb-2">
                            <AlertCircle className="w-4 h-4" /> Compliance Issues
                          </div>
                          <ul className="text-sm text-red-400 space-y-1">
                            {deal.compliance_issues.map((issue, idx) => (
                              <li key={idx}>• {issue}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* AI Recommendations */}
                      {deal.ai_recommendations?.length > 0 && (
                        <div className="mt-4 p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
                          <div className="flex items-center gap-2 text-amber-500 font-medium mb-2">
                            <Sparkles className="w-4 h-4" /> AI Recommendations
                          </div>
                          <ul className="text-sm text-amber-400 space-y-1">
                            {deal.ai_recommendations.map((rec, idx) => (
                              <li key={idx}>• {rec}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Digital Workers Tab */}
          <TabsContent value="workers" className="space-y-6">
            <div className="grid md:grid-cols-2 gap-6">
              {workers.map((worker) => (
                <Card key={worker.id} className="hover:border-amber-500/30 transition-all group" data-testid={`worker-${worker.id}`}>
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Bot className="w-6 h-6 text-amber-500" />
                      </div>
                      <Badge className="bg-emerald-500/10 text-emerald-500">{worker.status}</Badge>
                    </div>
                    <CardTitle className="text-lg mt-3">{worker.name}</CardTitle>
                    <CardDescription className="text-amber-400">{worker.subtitle}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-muted-foreground">{worker.description}</p>
                    
                    <div className="space-y-2">
                      {worker.features?.map((feature, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-xs text-muted-foreground">
                          <ChevronRight className="w-3 h-3 text-amber-500" />
                          {feature}
                        </div>
                      ))}
                    </div>

                    {worker.metrics && (
                      <div className="grid grid-cols-3 gap-2 pt-3 border-t border-border">
                        {Object.entries(worker.metrics).map(([key, value]) => (
                          <div key={key} className="text-center">
                            <div className="text-sm font-bold text-foreground">{value}</div>
                            <div className="text-xs text-muted-foreground capitalize">{key.replace(/_/g, ' ')}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <AIChat isOpen={showChat} onClose={() => setShowChat(false)} vertical="fi" />
      
      {!showChat && (
        <button onClick={() => setShowChat(true)} className="fixed bottom-6 right-6 w-14 h-14 rounded-full btn-ai flex items-center justify-center shadow-lg hover:scale-110 transition-transform" style={{ zIndex: 9998 }} data-testid="chat-fab">
          <MessageSquare className="w-6 h-6" />
        </button>
      )}
    </div>
  );
}
